import React from 'react';
import './LoadingSkeleton.css';

/**
 * Generic loading skeleton component
 */
export const LoadingSkeleton = ({ width = '100%', height = '20px', className = '' }) => {
  return (
    <div 
      className={`loading-skeleton ${className}`}
      style={{ width, height }}
    />
  );
};

/**
 * Loading skeleton for device info table
 */
export const DeviceInfoSkeleton = () => {
  return (
    <div className="device-info-skeleton">
      {Array.from({ length: 10 }).map((_, index) => (
        <div key={index} className="device-info-skeleton-row">
          <LoadingSkeleton width="120px" height="16px" />
          <LoadingSkeleton width="200px" height="16px" />
        </div>
      ))}
    </div>
  );
};

/**
 * Loading skeleton for pie charts
 */
export const PieChartSkeleton = ({ title }) => {
  return (
    <div className="pie-chart-skeleton">
      <div className="pie-chart-skeleton-title">
        {title ? (
          <span className="pie-chart-skeleton-title-text">{title}</span>
        ) : (
          <LoadingSkeleton width="80%" height="14px" />
        )}
      </div>
      <div className="pie-chart-skeleton-chart">
        <div className="pie-chart-skeleton-circle" />
      </div>
      <div className="pie-chart-skeleton-legend">
        {Array.from({ length: 3 }).map((_, index) => (
          <div key={index} className="pie-chart-skeleton-legend-item">
            <LoadingSkeleton width="8px" height="8px" className="skeleton-circle" />
            <LoadingSkeleton width="60px" height="10px" />
          </div>
        ))}
      </div>
    </div>
  );
};

/**
 * Loading skeleton for time series charts
 */
export const TimeSeriesChartSkeleton = ({ title }) => {
  return (
    <div className="chart-skeleton">
      <div className="chart-skeleton-title">
        <LoadingSkeleton width="60%" height="16px" />
      </div>
      <div className="chart-skeleton-content">
        <div className="chart-skeleton-lines">
          {Array.from({ length: 4 }).map((_, index) => (
            <div 
              key={index} 
              className="chart-skeleton-line"
              style={{ 
                left: `${index * 25}%`,
                animationDelay: `${index * 0.2}s`
              }}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

/**
 * Loading skeleton for histogram charts
 */
export const HistogramChartSkeleton = ({ title }) => {
  return (
    <div className="chart-skeleton">
      <div className="chart-skeleton-title">
        <LoadingSkeleton width="60%" height="16px" />
      </div>
      <div className="chart-skeleton-content">
        <div className="histogram-skeleton-bars">
          {Array.from({ length: 8 }).map((_, index) => (
            <div 
              key={index} 
              className="histogram-skeleton-bar"
              style={{ 
                height: `${Math.random() * 60 + 20}%`,
                animationDelay: `${index * 0.1}s`
              }}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

/**
 * Loading skeleton for stats cards
 */
export const StatsCardSkeleton = ({ title }) => {
  return (
    <div className="stats-card-skeleton">
      <div className="stats-card-skeleton-title">
        <LoadingSkeleton width="70%" height="16px" />
      </div>
      <div className="stats-card-skeleton-content">
        {Array.from({ length: 3 }).map((_, index) => (
          <div key={index} className="stats-card-skeleton-item">
            <LoadingSkeleton width="80px" height="14px" />
            <LoadingSkeleton width="40px" height="18px" />
          </div>
        ))}
      </div>
    </div>
  );
};

/**
 * Loading skeleton for event time series
 */
export const EventTimeSeriesSkeleton = ({ title }) => {
  return (
    <div className="chart-skeleton">
      <div className="chart-skeleton-title">
        <LoadingSkeleton width="60%" height="16px" />
      </div>
      <div className="chart-skeleton-content">
        <div className="event-skeleton-dots">
          {Array.from({ length: 12 }).map((_, index) => (
            <div 
              key={index} 
              className="event-skeleton-dot"
              style={{ 
                left: `${Math.random() * 90 + 5}%`,
                top: `${Math.random() * 60 + 20}%`,
                animationDelay: `${index * 0.15}s`
              }}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

/**
 * Loading skeleton for anomaly scatter chart
 */
export const AnomalyScatterSkeleton = ({ title }) => {
  return (
    <div className="chart-skeleton">
      <div className="chart-skeleton-title">
        <LoadingSkeleton width="60%" height="16px" />
      </div>
      <div className="chart-skeleton-content">
        <div className="scatter-skeleton-points">
          {Array.from({ length: 8 }).map((_, index) => (
            <div 
              key={index} 
              className="scatter-skeleton-point"
              style={{ 
                left: `${Math.random() * 80 + 10}%`,
                top: `${Math.random() * 60 + 20}%`,
                animationDelay: `${index * 0.2}s`
              }}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

/**
 * No data state component
 */
export const NoDataState = ({ message = "No data available", icon = "📊" }) => {
  return (
    <div className="no-data-state">
      <div className="no-data-icon">{icon}</div>
      <div className="no-data-message">{message}</div>
    </div>
  );
};

/**
 * No device selected state
 */
export const NoDeviceState = () => {
  return (
    <div className="no-device-state">
      <div className="no-device-icon">🔍</div>
      <div className="no-device-title">No Device Selected</div>
      <div className="no-device-message">
        Enter a device ID in the search box above to view device reports and analytics.
      </div>
    </div>
  );
};

/**
 * Error state component
 */
export const ErrorState = ({ error, onRetry }) => {
  return (
    <div className="error-state">
      <div className="error-icon">⚠️</div>
      <div className="error-title">Failed to Load Data</div>
      <div className="error-message">{error}</div>
      {onRetry && (
        <button className="error-retry-button" onClick={onRetry}>
          Try Again
        </button>
      )}
    </div>
  );
};

export default LoadingSkeleton;
